import { createEffect, createMemo, createSignal, For, onCleanup, Show } from "solid-js";

import { VerticalSwitch } from "~/js/components/vertical-switch";
import { game } from "~/js/game";
import { getInventoryValue, getItem, ItemCard, sellItems } from "~/js/items";

import { getCurrency } from "~/js/prefs";
import select from "~/js/select";
import { alwaysCompact, setAlwaysCompact } from "~/js/settings";
import { createPage } from "~/js/utils";
import { numToValuta } from "~/script";
import type { ItemName } from "~/types";
import { user } from "~/userStore";

// ---------------- Sorting ----------------
const sortModeLabels = {
	priceDescending: "Decreasing Price",
	priceAscending: "Increasing Price",
	rarityDescending: "Decreasing Rarity",
	rarityAscending: "Increasing Rarity",
	qualityDescending: "Quality Descending",
	qualityAscending: "Quality Ascending",
	"A-Z": "Alphabetic",
	"Z-A": "Alphabetic Reverse",
	casesFirst: "Cases First",
} as const;
export type SortMode = keyof typeof sortModeLabels;

// ---------------- Component ----------------
function InventoryPage() {
	// UI state
	const [showFavorites, setShowFavorites] = createSignal(false);
	const [sortMode, setSortMode] = createSignal<SortMode>("casesFirst");
	// Track absolute item index (start of current window) so resizing preserves context
	const [itemIndex, setItemIndex] = createSignal(0);
	const [searchOpen, setSearchOpen] = createSignal(false);
	const [searchTerm, setSearchTerm] = createSignal(""); // normalized
	const [sellDialogOpen, setSellDialogOpen] = createSignal(false);

	const [dimensionTick, setDimensionTick] = createSignal(0);
	const [pageEditMode, setPageEditMode] = createSignal(false);

	// Advanced sell dialog state
	// modes: under | rarity | nth | all | keys
	const [sellMode, setSellMode] = createSignal<"under" | "rarity" | "nth" | "all" | "keys">("keys");
	const [sellUnderValueCents, setSellUnderValueCents] = createSignal(1_000); // 10.00 default
	const [sellRarity, setSellRarity] = createSignal(0);
	const [sellNthPosition, setSellNthPosition] = createSignal(0); // 1-based
	const [sellNthSuffix, setSellNthSuffix] = createSignal("-th item in your inventory");

	// Resize handling (pagination size depends on dimensions)
	function handleResize() {
		setDimensionTick((v) => v + 1);
	}
	window.addEventListener("resize", handleResize);
	onCleanup(() => window.removeEventListener("resize", handleResize));

	// Source names list (reactive to user inventory/favorites and filters)
	const filteredNames = createMemo(() => {
		user.favinv ??= [];
		let names = showFavorites() ? [...user.favinv] : [...user.inventory];
		const term = searchTerm();
		if (term) {
			names = names.filter((nm) =>
				nm
					.toLowerCase()
					.replace(/[^a-z0-9]/g, "")
					.includes(term),
			);
		}
		return names;
	});

	// Aggregation + sorting + pagination prep
	interface DisplayEntry {
		name: ItemName;
		amount: number;
	}
	const aggregated = createMemo(() => {
		const names = filteredNames();
		const aggregateCounts: Record<string, number> = {};
		const aggregateEligibility: Record<string, boolean> = {};
		const displayItems: ItemName[] = [];
		let displayCount = names.length;

		for (const nm of names) {
			if (aggregateEligibility[nm] === undefined) {
				const it = getItem(nm);
				if (it.type === "case" || it.type === "capsule" || it.type === "key") {
					aggregateEligibility[nm] = true;
				} else {
					aggregateEligibility[nm] = false;
					displayItems.push(nm);
					continue;
				}
			}
			if (!aggregateEligibility[nm]) {
				displayItems.push(nm);
				continue;
			}
			if (aggregateCounts[nm]) {
				aggregateCounts[nm]! += 1;
				displayCount -= 1;
				continue;
			}
			aggregateCounts[nm] = 1;
			displayItems.push(nm);
		}

		const mode = sortMode();
		if (mode === "priceDescending") displayItems.sort((a, b) => getItem(b).price - getItem(a).price);
		else if (mode === "priceAscending") displayItems.sort((a, b) => getItem(a).price - getItem(b).price);
		else if (mode.endsWith("Ascending")) displayItems.sort((a, b) => getItem(a).price - getItem(b).price);
		else displayItems.sort((a, b) => getItem(b).price - getItem(a).price);

		if (mode === "rarityDescending") displayItems.sort((a, b) => getItem(b).rarity - getItem(a).rarity);
		else if (mode === "rarityAscending") displayItems.sort((a, b) => getItem(a).rarity - getItem(b).rarity);
		else if (mode === "qualityDescending") displayItems.sort((a, b) => (getItem(b) as any).quality - (getItem(a) as any).quality);
		else if (mode === "qualityAscending") displayItems.sort((a, b) => (getItem(a) as any).quality - (getItem(b) as any).quality);
		else if (mode === "A-Z")
			displayItems.sort((a, b) => {
				const an = getItem(a).name;
				const bn = getItem(b).name;
				return an > bn ? 1 : an < bn ? -1 : 0;
			});
		else if (mode === "Z-A")
			displayItems.sort((a, b) => {
				const an = getItem(a).name;
				const bn = getItem(b).name;
				return an > bn ? -1 : an < bn ? 1 : 0;
			});
		else if (mode === "casesFirst") {
			const grouped: ItemName[][] = [[], [], []];
			for (const nm of displayItems) {
				const t = getItem(nm).type;
				const idx = t === "case" || t === "capsule" ? 0 : t === "key" ? 1 : 2;
				grouped[idx]!.push(nm);
			}
			displayItems.splice(0, displayItems.length, ...grouped.flat());
		}

		return {
			displayCount,
			entries: displayItems.map<DisplayEntry>((n) => ({ name: n, amount: aggregateCounts[n] || 1 })),
		};
	});

	// Items per page (reuse legacy computation) - recompute on resize / compact changes
	const itemsPerPage = createMemo(() => {
		void dimensionTick(); // dependency
		const pagesEl = select("#pages");
		const pagesWidth = pagesEl ? pagesEl.offsetWidth : window.innerWidth;
		return alwaysCompact() || window.innerHeight <= 850 || window.innerWidth <= 1150
			? Math.max(1, Math.floor((window.innerHeight - 55) / 192)) * Math.max(1, Math.floor(pagesWidth / 112))
			: Math.max(1, Math.floor((window.innerHeight - 55) / 265)) * Math.max(1, Math.floor(pagesWidth / 177));
	});

	// Page total + enforce bounds (derive page from itemIndex so resizing keeps context)
	const pageTotal = createMemo(() => {
		const total = aggregated().displayCount / itemsPerPage();
		return Math.max(1, Math.ceil(total || 1));
	});
	const pageIndex = createMemo(() => Math.min(pageTotal() - 1, Math.max(0, Math.floor(itemIndex() / itemsPerPage()))));
	createEffect(() => {
		const maxStart = (pageTotal() - 1) * itemsPerPage();
		if (itemIndex() > maxStart) setItemIndex(maxStart);
		if (itemIndex() < 0) setItemIndex(0);
	});

	// Current page slice (uses itemIndex for stability across resize)
	const pageEntries = createMemo(() => {
		const start = itemIndex();
		const end = start + itemsPerPage();
		return aggregated().entries.slice(start, end);
	});

	// Inventory value text
	const inventoryValueText = createMemo(() => {
		if (showFavorites()) {
			return `Inv Value: ${numToValuta(getInventoryValue(true), 2)}\nFav-only Value: ${numToValuta(getInventoryValue(true, true), 2)}`;
		}
		return `Inv Value: ${numToValuta(getInventoryValue(true), 2)}`;
	});

	// Quick sell helpers
	function quickSellUnder(threshold: number) {
		sellItems(user.inventory.filter((id) => getItem(id).price < threshold));
	}

	// Advanced sell execution
	function executeSellDialog() {
		if (sellMode() === "under") {
			sellItems(user.inventory.filter((id) => getItem(id).price < sellUnderValueCents()));
		} else if (sellMode() === "rarity") {
			sellItems(user.inventory.filter((id) => getItem(id).rarity === sellRarity()));
		} else if (sellMode() === "nth") {
			const sorted = [...user.inventory].sort((a, b) => getItem(b).price - getItem(a).price);
			const nthItem = sorted[sellNthPosition() - 1];
			if (nthItem) {
				const nthPrice = getItem(nthItem).price;
				sellItems(user.inventory.filter((id) => getItem(id).price <= nthPrice));
			}
		} else if (sellMode() === "all") {
			sellItems(user.inventory);
		} else if (sellMode() === "keys") {
			sellItems(
				user.inventory.filter((id) => {
					const it = getItem(id);
					return it.type === "key" || it.type === "case" || it.type === "capsule";
				}),
			);
		}
		setSellDialogOpen(false);
	}

	// nth position suffix update
	createEffect(() => {
		const pos = sellNthPosition();
		const suffix = pos === 1 ? "-st" : pos === 2 ? "-nd" : "-th";
		const sorted = [...user.inventory].sort((a, b) => getItem(b).price - getItem(a).price);
		const nth = sorted[pos - 1];
		const nthPrice = nth ? getItem(nth).price : 0;
		setSellNthSuffix(`${suffix} item in your inventory (<= ${numToValuta(nthPrice)})`);
	});

	// Outside click for sell dialog
	function outsideClick(ev: MouseEvent) {
		if (!sellDialogOpen()) return;
		const dlg = document.querySelector(".mso-dialog-solid");
		if (dlg && !dlg.contains(ev.target as Node)) setSellDialogOpen(false);
	}
	window.addEventListener("mousedown", outsideClick);
	onCleanup(() => window.removeEventListener("mousedown", outsideClick));

	// Reset page when switching key state inputs
	createEffect(() => {
		showFavorites(); // dependency
		searchTerm();
		setItemIndex(0);
	});

	return (
		<div class="inventory-solid-root center">
			<div class="inventory-management-controls">
				{/* Compact mode toggle */}
				<VerticalSwitch checked={alwaysCompact()} label="Compact Mode" onChange={setAlwaysCompact} />

				{/* Sort dropdown */}
				<div class="css-dropdown">
					<button class="button" type="button">
						Sort: {sortModeLabels[sortMode()]}
					</button>
					<div class="css-dropdown-contents">
						{(Object.keys(sortModeLabels) as SortMode[]).map((m) => (
							<button class="button" onClick={() => setSortMode(m)} type="button">
								{sortModeLabels[m]}
							</button>
						))}
					</div>
				</div>

				{/* Sell dropdown */}
				<div class="css-dropdown">
					<button class="button" disabled={showFavorites()} type="button">
						Sell...
					</button>
					<div class="css-dropdown-contents">
						<button class="button" onClick={() => quickSellUnder(1_000)} type="button">
							Sell all under {numToValuta(1_000, 2)}
						</button>
						<button class="button" onClick={() => quickSellUnder(5_000)} type="button">
							Sell all under {numToValuta(5_000, 2)}
						</button>
						<button class="button" onClick={() => quickSellUnder(25_000)} type="button">
							Sell all under {numToValuta(25_000, 2)}
						</button>
						<button class="button" onClick={() => setSellDialogOpen(true)} type="button">
							More Options...
						</button>
					</div>
				</div>

				{/* Pagination controls */}
				<button
					class="button"
					disabled={pageIndex() === 0}
					onClick={() => setItemIndex(Math.max(0, itemIndex() - itemsPerPage()))}
					type="button"
				>
					&lt; Prev
				</button>
				<input
					class="num-input"
					onBlur={() => {
						setPageEditMode(false);
					}}
					onClick={(e) => e.currentTarget.select()}
					onFocus={() => setPageEditMode(true)}
					onInput={(e) => {
						if (!pageEditMode()) return; // only parse while editing
						const v = parseInt(e.currentTarget.value, 10) - 1;
						if (!Number.isNaN(v)) {
							const clampedPage = Math.min(pageTotal() - 1, Math.max(0, v));
							setItemIndex(clampedPage * itemsPerPage());
						}
					}}
					value={pageEditMode() ? String(pageIndex() + 1) : `${pageIndex() + 1} / ${pageTotal()}`}
				/>
				<button
					class="button"
					disabled={pageIndex() >= pageTotal() - 1}
					onClick={() => setItemIndex(Math.min((pageTotal() - 1) * itemsPerPage(), itemIndex() + itemsPerPage()))}
					type="button"
				>
					Next &gt;
				</button>

				{/* Favorites toggle */}
				<button
					class="button"
					innerHTML={showFavorites() ? "Back" : '<img src="images/favorites.png">'}
					onClick={() => setShowFavorites((v) => !v)}
					type="button"
				/>

				{/* Search toggle / input */}
				<button
					class="button"
					innerHTML={'<img src="images/search.png">'}
					onClick={() => {
						setSearchOpen((o) => !o);
						if (!searchOpen()) setSearchTerm("");
					}}
					type="button"
				/>
				<Show when={searchOpen()}>
					<input
						autofocus
						class="input imc-search-input"
						onInput={(e) => setSearchTerm(e.currentTarget.value.toLowerCase().replace(/[^a-z0-9]/g, ""))}
						placeholder="Search"
					/>
				</Show>

				{/* Inventory value */}
				<Show when={!searchOpen()}>
					<div class="imc-text" style={{ display: "flex", "white-space": "pre-line" }}>
						{inventoryValueText()}
					</div>
				</Show>
			</div>

			{/* Inventory container */}
			<div class="inventory-container">
				<Show
					fallback={
						<For each={pageEntries()}>
							{(entry) => {
								const amt = entry.amount > 1 ? entry.amount : null;
								return (
									<ItemCard
										favorite={showFavorites()}
										item={getItem(entry.name)}
										owned
										sell={!showFavorites()}
										{...(amt ? { amount: amt } : {})}
										noOptionalCompact={alwaysCompact()}
									/>
								);
							}}
						</For>
					}
					when={pageEntries().length === 0}
				>
					<div class="inventory-note">
						{showFavorites() ? "You have no favorites" : "Your inventory is empty, go to the shop to buy cases."}
					</div>
				</Show>
			</div>

			{/* Advanced Sell Dialog */}
			<Show when={sellDialogOpen()}>
				<div
					class="dialog mso-dialog "
					style={{ position: "absolute", inset: "0", display: "flex", "align-items": "center", "justify-content": "center" }}
				>
					<div class="dialog-body" style={{ background: "#111", padding: "16px", width: "420px", "max-width": "100%" }}>
						<div class="dialog-header">Sell Options</div>
						<div class="mso-row">
							<label style={{ display: "flex", "align-items": "center", gap: "6px" }}>
								<input checked={sellMode() === "under"} name="mso-mode" onInput={() => setSellMode("under")} type="radio" />
								<span>Sell everything under</span>
								<input
									class="mso-inline-input"
									onInput={(e) => {
										const v = Math.round((parseFloat(e.currentTarget.value) || 0) * 100);
										setSellUnderValueCents(v);
										setSellMode("under");
									}}
									style={{ width: "70px" }}
									type="text"
									value={(sellUnderValueCents() / 100).toFixed(2)}
								/>
								<span>{getCurrency()}</span>
							</label>
						</div>

						<div class="mso-row">
							<label style={{ display: "flex", "align-items": "center", gap: "6px" }}>
								<input checked={sellMode() === "rarity"} name="mso-mode" onInput={() => setSellMode("rarity")} type="radio" />
								<span>Sell everything with</span>
								<select
									class="mso-inline-select"
									onInput={(e) => {
										setSellRarity(parseInt(e.currentTarget.value, 10));
										setSellMode("rarity");
									}}
									value={sellRarity()}
								>
									<option style={{ color: "#fff" }} value={0}>
										Consumer (White)
									</option>
									<option style={{ color: "#99f" }} value={1}>
										Industrial (Light Blue)
									</option>
									<option style={{ color: "#33f" }} value={2}>
										Milspec (Blue)
									</option>
									<option style={{ color: "#84f" }} value={3}>
										Restricted (Purple)
									</option>
									<option style={{ color: "#f5f" }} value={4}>
										Classified (Magenta)
									</option>
									<option style={{ color: "#f22" }} value={5}>
										Covert (Red)
									</option>
									<option style={{ color: "#fd0" }} value={6}>
										Exceedingly Rare (Yellow)
									</option>
								</select>
								<span>rarity</span>
							</label>
						</div>

						<div class="mso-row">
							<label style={{ display: "flex", "align-items": "center", gap: "6px" }}>
								<input checked={sellMode() === "nth"} name="mso-mode" onInput={() => setSellMode("nth")} type="radio" />
								<span>Sell everything cheaper or equal to the</span>
								<input
									class="mso-inline-input"
									onInput={(e) => {
										const v = parseInt(e.currentTarget.value, 10) || 0;
										setSellNthPosition(v);
										setSellMode("nth");
									}}
									style={{ width: "40px" }}
									type="text"
									value={sellNthPosition() || ""}
								/>
								<span>{sellNthSuffix()}</span>
							</label>
						</div>

						<div class="mso-row">
							<label style={{ display: "flex", "align-items": "center", gap: "6px" }}>
								<input checked={sellMode() === "all"} name="mso-mode" onInput={() => setSellMode("all")} type="radio" />
								<span>Sell everything</span>
							</label>
						</div>

						<div class="mso-row">
							<label style={{ display: "flex", "align-items": "center", gap: "6px" }}>
								<input checked={sellMode() === "keys"} name="mso-mode" onInput={() => setSellMode("keys")} type="radio" />
								<span>Sell keys and cases</span>
							</label>
						</div>

						<div style={{ display: "flex", gap: "8px", "justify-content": "flex-end", "margin-top": "12px" }}>
							<button class="button" onClick={executeSellDialog} type="button">
								Sell
							</button>
							<button class="button dialog-close" onClick={() => setSellDialogOpen(false)} type="button">
								Close
							</button>
						</div>
					</div>
				</div>
			</Show>
		</div>
	);
}

createPage(() => <InventoryPage />, "inventory");
game.emit("update");
